import { useEffect, useState } from 'react';
import { Shield, CheckCircle, XCircle, Calendar } from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Avatar } from '@/components/ui/Avatar';
import { Modal } from '@/components/ui/Modal';
import { Textarea } from '@/components/ui/Textarea';
import { Spinner } from '@/components/ui/Spinner';
import api from '@/lib/api';
import type { ApiResponse } from '@hirenfire/shared';

interface KYCRequest {
  id: string;
  userId: string;
  displayName: string;
  email: string;
  avatar: string | null;
  status: string;
  requestedAt: string;
  scheduledAt: string | null;
}

export function AdminKYCPage() {
  const [requests, setRequests] = useState<KYCRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<KYCRequest | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const fetchRequests = async () => {
    try {
      const res = await api.get<ApiResponse<KYCRequest[]>>('/admin/kyc/pending');
      if (res.data.data) setRequests(res.data.data);
    } catch {
      // fallback
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const handleApprove = async (requestId: string) => {
    try {
      await api.post(`/admin/kyc/${requestId}/approve`);
      fetchRequests();
    } catch {
      // handle error
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) return;
    try {
      await api.post(`/admin/kyc/${selectedRequest.id}/reject`, { reason: rejectReason });
      setShowRejectModal(false);
      setRejectReason('');
      setSelectedRequest(null);
      fetchRequests();
    } catch {
      // handle error
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-white">KYC Reviews</h1>
        <p className="text-gray-400 mt-1">Review and approve identity verification requests</p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Spinner size="lg" />
        </div>
      ) : requests.length === 0 ? (
        <Card>
          <CardBody className="py-16 text-center">
            <Shield className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No pending KYC requests</p>
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-3">
          {requests.map((req) => (
            <Card key={req.id} className="hover:border-dark-500 transition-all">
              <CardBody>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Avatar src={req.avatar} name={req.displayName} size="lg" />
                    <div>
                      <p className="font-semibold text-white">{req.displayName}</p>
                      <p className="text-sm text-gray-400">{req.email}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <Badge variant="warning">{req.status}</Badge>
                        <span className="flex items-center gap-1 text-xs text-gray-500">
                          <Calendar className="w-3 h-3" />
                          Requested {new Date(req.requestedAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleApprove(req.id)}
                      leftIcon={<CheckCircle className="w-4 h-4" />}
                      className="text-success-400 hover:text-success-300"
                    >
                      Approve
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedRequest(req);
                        setShowRejectModal(true);
                      }}
                      leftIcon={<XCircle className="w-4 h-4" />}
                      className="text-error-400 hover:text-error-300"
                    >
                      Reject
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      <Modal
        isOpen={showRejectModal}
        onClose={() => {
          setShowRejectModal(false);
          setSelectedRequest(null);
          setRejectReason('');
        }}
        title="Reject KYC Request"
      >
        <div className="space-y-4">
          <p className="text-gray-400 text-sm">
            Rejecting verification for{' '}
            <span className="text-white font-medium">{selectedRequest?.displayName}</span>
          </p>
          <Textarea
            label="Rejection Reason"
            placeholder="Explain why the request is being rejected..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setShowRejectModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleReject}>
              Confirm Rejection
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
